import React, { useEffect, useState } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  Badge,
  Row,
  Col,
  Button,
  Alert,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner,
  Table,
  Label,
  Input,
  Progress,
} from "reactstrap";
import Select from "react-select";

import { useCreatePurchaseOrderMutation } from 'slices/InventoryManagement/inventoryManagementSlice';
import { useGetProjectBudgetsByProjectIdQuery } from 'slices/projects/projectSlice';
import { IsValidPermissions } from 'Routes/AuthProtected';
import PriceViewComponent, { PriceViewCompact } from '../CommonComponents/PriceViewComponent';
import ProductThumbnail from '../CommonComponents/ProductThumbnail';

interface GroupedItems {
  [key: string]: any[];
}

interface PreviewSectionProps {
  purchaseOrder: any;
  onClosePreview?: () => void;
  onOrderCreated?: () => void; // Callback for when order is created
  purchaseOrdersDataRefetch?: () => void;
}

const STOCK_ONLY_GROUP_KEY = "__stock_transfer_only__";
const STOCK_ONLY_GROUP_LABEL = "Stock Transfer (No Supplier)";

const PreviewSection: React.FC<PreviewSectionProps> = ({
  purchaseOrder,
  onClosePreview,
  onOrderCreated,
  purchaseOrdersDataRefetch
}) => {
  const [createPurchaseOrder, { isLoading: isCreatingPO }] = useCreatePurchaseOrderMutation();
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<string | null>(null);
  const [createdOrders, setCreatedOrders] = useState<Set<number>>(new Set()); // Track created PO IDs
  const [selectedBudgetSectionId, setSelectedBudgetSectionId] = useState<number | "">("");
  const [selectedBudgetSection, setSelectedBudgetSection] = useState<any>(null);
  const [poNotes, setPoNotes] = useState<string[]>([""]);
  const [overBudgetNotes, setOverBudgetNotes] = useState<string>("");

  const projectId = purchaseOrder?.project ?? purchaseOrder?.project_id;
  const [budgetSearch, setBudgetSearch] = useState("");
  const [budgetPage, setBudgetPage] = useState(1);
  const budgetPageSize = 20; // Pagination for large datasets
  
  const { data: budgetData } = useGetProjectBudgetsByProjectIdQuery(
    { projectId: String(projectId), page: budgetPage, pageSize: budgetPageSize, search: budgetSearch },
    { skip: !projectId }
  );
  const budgetSections = budgetData?.results ?? [];
  const budgetTotalPages = budgetData?.total_page ?? 1;

  const [currentUserEmployeeId, setCurrentUserEmployeeId] = useState<number | null>(null); // Added state for user ID

  // Fetch current user from localStorage
  useEffect(() => {
    const fetchAuthUser = () => {
      const authUserString = localStorage.getItem('authUser');
      if (authUserString) {
        try {
          const authUser = JSON.parse(authUserString);
          setCurrentUserEmployeeId(authUser?.employee_id || null);
        } catch (error) {
          console.error('Error parsing auth user:', error);
        }
      }
    };
    fetchAuthUser();
  }, []);

  // Group items by supplier - mapped suppliers first, then unmapped products
  const groupItemsBySupplier = (): { 
    mapped: GroupedItems, 
    unmapped: any[],
    rfqItems: any[]
  } => {
    if (!purchaseOrder?.items) return { mapped: {}, unmapped: [], rfqItems: [] };

    const grouped: GroupedItems = {};
    const unmapped: any[] = [];
    const rfqItems: any[] = [];

    purchaseOrder.items.forEach((item: any) => {
      const sourceMode = String(item?.source_mode || "").toLowerCase();
      const stockQty = parseFloat(item?.stock_allocation_quantity || 0) || 0;
      const supplierQty = parseFloat(item?.supplier_allocation_quantity || 0) || 0;
      const isStockOnly = sourceMode === "stock" || (stockQty > 0 && supplierQty <= 0);

      if (isStockOnly) {
        if (!grouped[STOCK_ONLY_GROUP_KEY]) {
          grouped[STOCK_ONLY_GROUP_KEY] = [];
        }
        grouped[STOCK_ONLY_GROUP_KEY].push(item);
      } else if (item.supplier_name) {
        const supplierKey = item.supplier_name;
        if (!grouped[supplierKey]) {
          grouped[supplierKey] = [];
        }
        grouped[supplierKey].push(item);
      } else if (item.is_rfq) {
        rfqItems.push(item);
      } else {
        unmapped.push(item);
      }
    });

    return { mapped: grouped, unmapped, rfqItems };
  };

  const { mapped: groupedItems, unmapped, rfqItems } = groupItemsBySupplier();

  // For split source_mode, supplier totals should use only supplier portion.
  const getSupplierQuantityForItem = (item: any) => {
    const sourceMode = String(item?.source_mode || "").toLowerCase();
    if (sourceMode === "split") {
      return parseFloat(item?.supplier_allocation_quantity || 0) || 0;
    }
    if (sourceMode === "stock") {
      return 0;
    }
    const supplierQty = parseFloat(item?.supplier_allocation_quantity || 0) || 0;
    if (supplierQty > 0) return supplierQty;
    return parseFloat(item?.quantity || 0) || 0;
  };

  const getPreviewQuantityForItem = (item: any) => {
    const sourceMode = String(item?.source_mode || "").toLowerCase();
    if (sourceMode === "stock") {
      return parseFloat(item?.stock_allocation_quantity || item?.quantity || 0) || 0;
    }
    return getSupplierQuantityForItem(item);
  };

  // Calculate supplier-wise totals
  const calculateSupplierTotals = (items: any[]) => {
    const quantity = items.reduce(
      (total, item) => total + getPreviewQuantityForItem(item),
      0,
    );
    const cost = items.reduce((total, item) => total + parseFloat(item.total_item_cost || 0), 0);
    return { quantity, cost };
  };

  // Calculate total cost
  const calculateTotalCost = () => {
    if (!purchaseOrder?.items) return 0;
    return purchaseOrder.items.reduce((total: number, item: any) => {
      return total + parseFloat(item.total_item_cost || 0);
    }, 0);
  };

  // Calculate total items quantity
  const calculateTotalQuantity = () => {
    if (!purchaseOrder?.items) return 0;
    return purchaseOrder.items.reduce((total: number, item: any) => {
      return total + parseFloat(item.quantity || 0);
    }, 0);
  };

  // Total quantity that will be sourced from suppliers (excludes stock-only portion)
  const calculateTotalSupplierQuantity = () => {
    if (!purchaseOrder?.items) return 0;
    return purchaseOrder.items.reduce(
      (total: number, item: any) => total + getSupplierQuantityForItem(item),
      0,
    );
  };

  const formatDateTime = (dateString: string, type: "date" | "datetime" = "datetime") => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  };

  // Check if all items for a supplier have PO created
  const isSupplierPOComplete = (supplierItems: any[]) => {
    return supplierItems.every(item => item.is_po_created === true);
  };

  // Check if supplier has any items that need PO creation
  const hasItemsForPOCreation = (supplierItems: any[]) => {
    return supplierItems.some(item => item.is_po_created !== true);
  };

  // Get supplier-specific item IDs that don't have PO created yet
  const getSupplierItemIdsForPO = (supplierItems: any[]) => {
    return supplierItems
      .filter(item => !item.is_po_created) // Only include items without PO
      .map(item => item.id);
  };

  // Get items that are eligible for PO creation (without existing PO)
  const getEligibleItemsForPO = (supplierItems: any[]) => {
    return supplierItems.filter(item => !item.is_po_created);
  };

  // Get items that already have PO created
  const getItemsWithExistingPO = (supplierItems: any[]) => {
    return supplierItems.filter(item => item.is_po_created);
  };

  // Handle Create Purchase Order button click
  const handleCreatePO = (supplierName: string) => {
    setSelectedSupplier(supplierName);
    setConfirmationModal(true);
  };

  // Confirm and create purchase order
  const handleConfirmCreatePO = async () => {
    if (!selectedSupplier || !purchaseOrder?.id) return;
    const isStockOnlyGroup = selectedSupplier === STOCK_ONLY_GROUP_KEY;

    const supplierItems = groupedItems[selectedSupplier];
    if (!supplierItems || supplierItems.length === 0) return;

    // Get only items that don't have PO created yet
    const eligibleItemIds = getSupplierItemIdsForPO(supplierItems);
    
    if (eligibleItemIds.length === 0) {
      console.log("No eligible items for PO creation");
      setConfirmationModal(false);
      setSelectedSupplier(null);
      return;
    }

    const normalizedNotes = poNotes
      .map((n) => n.trim())
      .filter((n) => n.length > 0);

    if (!isStockOnlyGroup && normalizedNotes.length === 0) {
      alert("Please enter at least one note / instruction for this purchase order.");
      return;
    }

    try {
      const requestData: any = {
        purchase_request_id: parseInt(purchaseOrder.id),
        item_ids: eligibleItemIds,
        po_status: isStockOnlyGroup ? "draft" : "submitted",
        submitted_by: currentUserEmployeeId 
      };
      if (!isStockOnlyGroup && budgetSections.length > 0) {
        if (!selectedBudgetSectionId) {
          return;
        }
        requestData.project_budget_id = selectedBudgetSectionId;
      } else if (!isStockOnlyGroup && selectedBudgetSectionId) {
        requestData.project_budget_id = selectedBudgetSectionId;
      }
      // Backend expects notes as JSON array; send each note as a separate entry
      requestData.notes = normalizedNotes;
      // Dedicated overbudget notes field on PO (separate from general notes)
      if (!isStockOnlyGroup && overBudgetNotes.trim()) {
        requestData.overbudget_notes = overBudgetNotes.trim();
      }

      console.log("Creating purchase order:", requestData);

      const result = await createPurchaseOrder(requestData).unwrap();
      if(purchaseOrdersDataRefetch){
        purchaseOrdersDataRefetch();
      }
      
      if (onOrderCreated) {
        onOrderCreated();
      }      
      console.log("Purchase order created:", result);

      // Add to created orders set
      if (result.id) {
        setCreatedOrders(prev => new Set(prev.add(result.id)));
      }

      // Close modal
      setConfirmationModal(false);
      setSelectedSupplier(null);
      setPoNotes([""]);
      setOverBudgetNotes("");

    } catch (error) {
      console.error("Failed to create purchase order:", error);
      // Handle error (show toast/alert)
    }
  };

  // Close confirmation modal
  const handleCloseConfirmation = () => {
    setConfirmationModal(false);
    setSelectedSupplier(null);
    setPoNotes([""]);
    setOverBudgetNotes("");
  };

  return (
    <>
      <Card className="mb-4">
        <CardHeader className="bg-light">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="card-title mb-0 text-primary">
              <i className="ri-shopping-bag-line align-middle me-2"></i>
              Purchase Order Preview
            </h5>
            <div className="d-flex gap-2">
              {/* Close Preview Button */}
              {onClosePreview && (
                <Button 
                  color="outline-secondary" 
                  size="sm" 
                  onClick={onClosePreview}
                >
                  <i className="ri-close-line align-middle me-1"></i>
                  Close Preview
                </Button>
              )}
              {/* <Button color="outline-primary" size="sm" onClick={() => window.print()}>
                <i className="ri-printer-line align-middle me-1"></i>
                Print
              </Button> */}
            </div>
          </div>
        </CardHeader>
        <CardBody>
          {/* Order Summary */}
          <Row className="mb-4 p-3 border rounded bg-light">
            <Col xl={3} lg={6}>
              <div className="mb-2">
                <strong className="text-muted">Title:</strong> 
                <span className="ms-2">{purchaseOrder?.title || "N/A"}</span>
              </div>
              <div className="mb-2">
                <strong className="text-muted">Project:</strong> 
                <span className="ms-2">{purchaseOrder?.project_name || "N/A"}</span>
              </div>
            </Col>
            <Col xl={3} lg={6}>
              <div className="mb-2">
                <strong className="text-muted">Total Items:</strong> 
                <span className="ms-2">{purchaseOrder?.items_count || 0}</span>
              </div>
              <div className="mb-2">
                <strong className="text-muted">Total Quantity:</strong> 
                <span className="ms-2">
                  Requested: {calculateTotalQuantity()} | Supplier Qty: {calculateTotalSupplierQuantity()}
                </span>
              </div>
            </Col>
            <Col xl={3} lg={6}>
              <div className="mb-2">
                <strong className="text-muted">Created:</strong> 
                <span className="ms-2">
                  {purchaseOrder?.created_at ? formatDateTime(purchaseOrder.created_at, "datetime") : "N/A"}
                </span>
              </div>
              {purchaseOrder?.request_submitted_date && (
                <div className="mb-2">
                  <strong className="text-muted">Submitted:</strong> 
                  <span className="ms-2">
                    {formatDateTime(purchaseOrder.request_submitted_date, "datetime")}
                  </span>
                </div>
              )}
            </Col>
            <Col xl={3} lg={6}>
              <div className="mb-2">
                <strong className="text-muted">Mapped Suppliers:</strong> 
                <span className="ms-2">
                  {Object.keys(groupedItems).length}
                </span>
              </div>
              <div className="mb-2">
                <strong className="text-muted">Grand Total:</strong> 
                <span className="ms-2 fw-bold text-primary fs-6">
                  <PriceViewCompact price={calculateTotalCost().toFixed(2)}/>
                </span>
              </div>
            </Col>
          </Row>

          {/* Mapped Suppliers Section */}
          {Object.keys(groupedItems).length > 0 && (
            <div className="mb-4">
              <h6 className="text-success mb-3">
                <i className="ri-checkbox-circle-line align-middle me-2"></i>
                Mapped Sources ({Object.keys(groupedItems).length})
              </h6>
              
              {Object.keys(groupedItems).map((supplierName) => {
                const isStockOnlyGroup = supplierName === STOCK_ONLY_GROUP_KEY;
                const supplierItems = groupedItems[supplierName];
                const totals = calculateSupplierTotals(supplierItems);
                const isPOComplete = isSupplierPOComplete(supplierItems);
                const hasEligibleItems = hasItemsForPOCreation(supplierItems);
                const eligibleItems = getEligibleItemsForPO(supplierItems);
                const existingPOItems = getItemsWithExistingPO(supplierItems);
                const isCreatingForThisSupplier = isCreatingPO && selectedSupplier === supplierName;
                
                return (
                  <Card key={supplierName} className={`mb-4 shadow-sm ${isPOComplete ? 'border-success' : 'border-primary'}`}>
                    <CardHeader className={`py-3 ${isPOComplete ? 'bg-success text-white' : 'bg-light border-primary'}`}>
                      <div className="d-flex justify-content-between align-items-center">
                        <h6 className="mb-0 fw-bold">
                          <i
                            className={`${isStockOnlyGroup ? "ri-box-3-line" : "ri-user-line"} align-middle me-2`}
                          ></i>
                          {isStockOnlyGroup
                            ? STOCK_ONLY_GROUP_LABEL
                            : `Supplier: ${supplierName}`}
                          {isPOComplete && (
                            <Badge color="light" className="ms-2 text-success">
                              <i className="ri-check-double-line align-middle me-1"></i>
                              All POs Created
                            </Badge>
                          )}
                          {!isPOComplete && existingPOItems.length > 0 && (
                            <Badge color="warning" className="ms-2">
                              <i className="ri-information-line align-middle me-1"></i>
                              Partial POs Created
                            </Badge>
                          )}
                        </h6>
                        <div className="d-flex align-items-center gap-3">
                          <Badge color={isPOComplete ? "success" : hasEligibleItems ? "primary" : "light"} className="fs-6">
                            {supplierItems.length} item(s)
                          </Badge>
                          {!isPOComplete && existingPOItems.length > 0 && (
                            <Badge color="outline-success" className="fs-6">
                              {existingPOItems.length} PO created
                            </Badge>
                          )}
                          <div className={`fs-6 ${isPOComplete ? 'text-white' : 'text-muted'}`}>
                            Total: <strong className={isPOComplete ? 'text-white' : 'text-primary'}><PriceViewCompact price={totals.cost.toFixed(2)}/></strong>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardBody className="p-0">
                      <div className="table-responsive">
                        <table className="table table-hover table-borderless mb-0">
                          <thead className={isPOComplete ? 'table-success' : 'table-primary'}>
                            <tr>
                              <th style={{ width: '45%' }} className="ps-4">Product Details</th>
                              <th style={{ width: '10%' }} className="text-center">Quantity</th>
                              <th style={{ width: '15%' }} className="text-center">Unit Price</th>
                              <th style={{ width: '15%' }} className="text-center">Discount</th>
                              <th style={{ width: '15%' }} className="text-end pe-4">Total Cost</th>
                            </tr>
                          </thead>
                          <tbody>
                            {supplierItems.map((item: any) => (
                              <tr key={item.id} className={`border-bottom ${item.is_po_created ? 'bg-light' : ''}`}>
                                <td className="ps-4">
                                  <div className="d-flex align-items-center">
                                    <ProductThumbnail
                                                                              attachment_urls={item?.attachment_urls}
                                                                              alt={item?.product_name || ''}
                                                                              size="lg"
                                                                              className="me-2"
                                                                            />
                                    {/* {item.thumbnail_url && (
                                      <img 
                                        src={item.thumbnail_url} 
                                        alt={item.product_name}
                                        className="rounded me-3"
                                        style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                                      />
                                    )} */}
                                    <div>
                                      <div className="fw-semibold text-dark">{item.product_name}</div>
                                      <div className="text-muted small">{item.category_name}</div>
                                      {item.tag_names && item.tag_names.length > 0 && (
                                        <div className="mt-1">
                                          {item.tag_names.map((tag: string, index: number) => (
                                            <Badge key={index} color="secondary" className="me-1" pill>
                                              {tag}
                                            </Badge>
                                          ))}
                                        </div>
                                      )}
                                      {item.is_po_created && (
                                        <Badge color="success" className="mt-1">
                                          <i className="ri-check-line align-middle me-1"></i>
                                          PO Created
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                </td>
                                <td className="text-center align-middle">
                                <span className="fw-semibold">{getPreviewQuantityForItem(item)}</span>
                                  <div className="text-muted small">{item.measurement_name}</div>
                                </td>
                                <td className="text-center align-middle">
                                <PriceViewCompact price={(parseFloat(item.total_item_cost || 0) / Math.max(getPreviewQuantityForItem(item), 1)).toFixed(2)}/>
                                </td>
                                <td className="text-center align-middle">
                                  <span className="text-muted">
                                    <PriceViewCompact price={parseFloat(item.discount || 0).toFixed(2)}/>
                                  </span>
                                </td>
                                <td className="text-end align-middle fw-bold text-primary pe-4">
                                  <PriceViewCompact price={parseFloat(item.total_item_cost).toFixed(2)}/>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-light">
                            <tr>
                              <td colSpan={3} className="text-end fw-bold ps-4">
                                {isStockOnlyGroup ? "Stock Transfer Summary:" : "Supplier Summary:"}
                              </td>
                              <td className="text-center fw-bold">
                                {totals.quantity} units
                              </td>
                              <td className="text-end fw-bold fs-5 text-primary pe-4">
                                <PriceViewCompact price={totals.cost.toFixed(2)}/>
                              </td>
                            </tr>
                            {!isPOComplete && existingPOItems.length > 0 && (
                              <tr>
                                <td colSpan={5} className="ps-4">
                                  <Alert color="info" className="mb-0 py-2">
                                    <i className="ri-information-line align-middle me-2"></i>
                                    <strong>Note:</strong> {existingPOItems.length} items already have purchase orders created. 
                                    Only {eligibleItems.length} new items will be included in this purchase order.
                                  </Alert>
                                </td>
                              </tr>
                            )}
                            <tr>
                              <td></td>
                              <td></td>
                              <td></td>
                              <td></td>
                              <td>
                                {hasEligibleItems ? (
                                  <>
                                  {IsValidPermissions(["ADD_PURCHASE_ORDER"])&&
                                  <Button 
                                    className={`btn btn-sm ${isStockOnlyGroup ? "btn-success" : "btn-primary"}`}
                                    onClick={() => handleCreatePO(supplierName)}
                                    disabled={isCreatingPO}
                                  >
                                    {isCreatingForThisSupplier ? (
                                      <>
                                        <Spinner size="sm" className="me-2" />
                                        Creating...
                                      </>
                                    ) : (
                                      <>
                                        {isStockOnlyGroup ? "Create Stock Transfer PO" : "Create Purchase Order"} <br/>
                                        {isStockOnlyGroup ? STOCK_ONLY_GROUP_LABEL : supplierName}
                                      </>
                                    )}
                                  </Button>
              }
                                  </>
                                ) : (
                                  <Button className='btn btn-success btn-sm' disabled>
                                    <i className="ri-check-double-line align-middle me-1"></i>
                                    All POs Created
                                  </Button>
                                )}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </CardBody>
                  </Card>
                );
              })}
            </div>
          )}

          <hr className="border-2 border-primary border-dashed" />

          {/* RFQ Items Section */}
          {rfqItems.length > 0 && (
            <div className="mb-4">
              <h6 className="text-info mb-3">
                <i className="ri-question-line align-middle me-2"></i>
                RFQ Items -  ({rfqItems.length})
              </h6>
              
              <Card className="shadow-sm border-info">
                <CardHeader className="bg-light py-3 border-info">
                  <div className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0 fw-bold text-info">
                      <i className="ri-information-line align-middle me-2"></i>
                      Request for Quotation Items
                    </h6>
                    <Badge color="info" className="fs-6">
                      {rfqItems.length} item(s)
                    </Badge>
                  </div>
                </CardHeader>
                <CardBody className="p-0">
                  <div className="table-responsive">
                    <table className="table table-hover table-borderless mb-0">
                      <thead className="table-info">
                        <tr>
                          <th style={{ width: '50%' }} className="ps-4">Product Details</th>
                          <th style={{ width: '15%' }} className="text-center">Quantity</th>
                          <th style={{ width: '20%' }} className="text-center">Expected Delivery</th>
                          <th style={{ width: '15%' }} className="text-center">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rfqItems.map((item: any) => (
                          <tr key={item.id} className="border-bottom">
                            <td className="ps-4">
                              <div className="d-flex align-items-center">
                                <ProductThumbnail
                                                                          attachment_urls={item?.attachment_urls}
                                                                          alt={item?.product_name || ''}
                                                                          size="lg"
                                                                          className="me-2"
                                                                        />
                                {/* {item.thumbnail_url && (
                                  <img 
                                    src={item.thumbnail_url} 
                                    alt={item.product_name}
                                    className="rounded me-3"
                                    style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                                  />
                                )} */}
                                <div>
                                  <div className="fw-semibold text-dark">{item.product_name}</div>
                                  <div className="text-muted small">{item.category_name}</div>
                                  {item.tag_names && item.tag_names.length > 0 && (
                                    <div className="mt-1">
                                      {item.tag_names.map((tag: string, index: number) => (
                                        <Badge key={index} color="secondary" className="me-1" pill>
                                          {tag}
                                        </Badge>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="text-center align-middle">
                              <span className="fw-semibold">{item.quantity}</span>
                              <div className="text-muted small">{item.measurement_name}</div>
                            </td>
                            <td className="text-center align-middle">
                              {item.expected_delivery_date ? (
                                <span className="badge bg-soft-success text-success">
                                  {new Date(item.expected_delivery_date).toLocaleDateString()}
                                </span>
                              ) : (
                                <span className="text-muted">Not specified</span>
                              )}
                            </td>
                            <td className="text-center align-middle">
                              <Badge color="info">RFQ - Supplier Needed</Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardBody>
              </Card>
            </div>
          )}

          {/* Unmapped Products Section */}
          {unmapped.length > 0 && (
            <div className="mb-4">
              <h6 className="text-warning mb-3">
                <i className="ri-alert-line align-middle me-2"></i>
                Unmapped Products ({unmapped.length})
              </h6>
              
              <Card className="shadow-sm border-warning">
                <CardHeader className="bg-light py-3 border-warning">
                  <div className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0 fw-bold text-warning">
                      <i className="ri-error-warning-line align-middle me-2"></i>
                      Products Need Supplier Assignment
                    </h6>
                    <Badge color="warning" className="fs-6">
                      {unmapped.length} item(s)
                    </Badge>
                  </div>
                </CardHeader>
                <CardBody className="p-0">
                  <div className="table-responsive">
                    <table className="table table-hover table-borderless mb-0">
                      <thead className="table-warning">
                        <tr>
                          <th style={{ width: '50%' }} className="ps-4">Product Details</th>
                          <th style={{ width: '15%' }} className="text-center">Quantity</th>
                          <th style={{ width: '20%' }} className="text-center">Expected Delivery</th>
                          <th style={{ width: '15%' }} className="text-center">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {unmapped.map((item: any) => (
                          <tr key={item.id} className="border-bottom">
                            <td className="ps-4">
                              <div className="d-flex align-items-center">
                                <ProductThumbnail
                                                                          attachment_urls={item?.attachment_urls}
                                                                          alt={item?.product_name || ''}
                                                                          size="lg"
                                                                          className="me-2"
                                                                        />
                                {/* {item.thumbnail_url && (
                                  <img 
                                    src={item.thumbnail_url} 
                                    alt={item.product_name}
                                    className="rounded me-3"
                                    style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                                  />
                                )} */}
                                <div>
                                  <div className="fw-semibold text-dark">{item.product_name}</div>
                                  <div className="text-muted small">{item.category_name}</div>
                                  {item.tag_names && item.tag_names.length > 0 && (
                                    <div className="mt-1">
                                      {item.tag_names.map((tag: string, index: number) => (
                                        <Badge key={index} color="secondary" className="me-1" pill>
                                          {tag}
                                        </Badge>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="text-center align-middle">
                              <span className="fw-semibold">{item.quantity}</span>
                              <div className="text-muted small">{item.measurement_name}</div>
                            </td>
                            <td className="text-center align-middle">
                              {item.expected_delivery_date ? (
                                <span className="badge bg-soft-success text-success">
                                  {new Date(item.expected_delivery_date).toLocaleDateString()}
                                </span>
                              ) : (
                                <span className="text-muted">Not specified</span>
                              )}
                            </td>
                            <td className="text-center align-middle">
                              <Badge color="warning">Needs Supplier</Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardBody>
              </Card>
            </div>
          )}

          {/* Overall Summary */}
          <div className="border-top pt-3 mt-4">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <strong className="text-muted">Overall Summary:</strong>
                <span className="ms-2">
                  {purchaseOrder?.items_count || 0} items • {calculateTotalQuantity()} units • 
                  <span className="fw-bold text-primary ms-1"><PriceViewCompact price={calculateTotalCost().toFixed(2)}/></span>
                </span>
                {unmapped.length > 0 && (
                  <div className="text-warning small mt-1">
                    <i className="ri-alert-line align-middle me-1"></i>
                    {unmapped.length} items still need supplier assignment
                  </div>
                )}
              </div>
              <div className="d-flex gap-2">
                {/* Close Preview Button in Footer */}
                {onClosePreview && (
                  <Button 
                    color="outline-secondary" 
                    onClick={onClosePreview}
                  >
                    <i className="ri-close-line align-middle me-1"></i>
                    Close Preview
                  </Button>
                )}
              </div>
            </div>
            {unmapped?.length > 0 && (
              <Alert color="warning" className="mt-3">
                <i className="ri-alert-line align-middle me-2"></i>
                Please assign suppliers to all products before submitting the order. There are {unmapped.length} products that still need supplier assignment.
              </Alert>
            )}
          </div>
        </CardBody>
      </Card>

      {/* Create Purchase Order Modal - redesigned with items table and required budget section */}
      <Modal isOpen={confirmationModal} toggle={handleCloseConfirmation} size="xl" centered>
        <ModalHeader toggle={handleCloseConfirmation} className="bg-light" style={{ backgroundColor: '#f8f9fa' }}>
          <div className="d-flex align-items-center gap-2 text-dark">
            <i className="ri-shopping-cart-line" />
            <span>
              Create Purchase Order —{" "}
              {selectedSupplier === STOCK_ONLY_GROUP_KEY
                ? STOCK_ONLY_GROUP_LABEL
                : selectedSupplier}
            </span>
          </div>
        </ModalHeader>
        <ModalBody className="p-4">
          {selectedSupplier && groupedItems[selectedSupplier] && (() => {
            const isStockOnlyGroup = selectedSupplier === STOCK_ONLY_GROUP_KEY;
            const eligibleItems = getEligibleItemsForPO(groupedItems[selectedSupplier]);
            const supplierTotals = calculateSupplierTotals(eligibleItems);
            const budgetOptions = budgetSections.map((s: any) => {
              const allocated = parseFloat(s.allocated_cost || 0);
              const remaining = parseFloat(s.remaining_cost || 0);
              const actual = parseFloat(s.actual_cost || 0);
              const utilizationPercent = allocated > 0 ? (actual / allocated) * 100 : 0;
              const isOverBudget = actual > allocated;
              const isNearLimit = utilizationPercent >= 90 && actual <= allocated;
              
              return {
                value: s.id,
                label: `${s.section_name} | Allocated: ${allocated.toFixed(2)} | Remaining: ${remaining.toFixed(2)}`,
                section: s,
                isOverBudget,
                isNearLimit,
                utilizationPercent,
              };
            });
            const selectedOption = budgetOptions.find((o: any) => o.value === selectedBudgetSectionId);
            const exceedsRemaining =
              selectedBudgetSection && supplierTotals.cost > parseFloat(selectedBudgetSection.remaining_cost || 0);

            // Normalize numeric values to avoid string comparison bugs
            const sectionAllocated = selectedBudgetSection
              ? parseFloat(selectedBudgetSection.allocated_cost || 0)
              : 0;
            const sectionActual = selectedBudgetSection
              ? parseFloat(selectedBudgetSection.actual_cost || 0)
              : 0;
            const selectedSectionUtilization =
              selectedBudgetSection && sectionAllocated > 0
                ? (sectionActual / sectionAllocated) * 100
                : 0;
            const isSectionOverBudget = sectionAllocated > 0 && sectionActual > sectionAllocated;

            return (
              <>
                {/* Items Summary Card */}
                <Card className="border-0 shadow-sm mb-4">
                  <CardHeader className="bg-light py-2" style={{ backgroundColor: '#f8f9fa' }}>
                    <h6 className="mb-0 d-flex align-items-center gap-2">
                      <i className="ri-file-list-3-line" />
                      Items to be included ({eligibleItems.length})
                    </h6>
                  </CardHeader>
                  <CardBody className="p-0">
                    <div className="table-responsive" style={{ maxHeight: "300px", overflowY: "auto" }}>
                      <Table size="sm" bordered className="mb-0">
                        <thead className="table-light sticky-top">
                          <tr>
                            <th>Product</th>
                            <th className="text-center">Quantity</th>
                            <th className="text-end">Unit Price</th>
                            <th className="text-end">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {eligibleItems.map((item: any) => (
                            <tr key={item.id}>
                              <td>{item.product_name}</td>
                              <td className="text-center">{getPreviewQuantityForItem(item)} {item.measurement_name || ""}</td>
                              <td className="text-end">
                                <PriceViewCompact price={(parseFloat(item.total_item_cost || 0) / Math.max(getPreviewQuantityForItem(item), 1)).toFixed(2)} />
                              </td>
                              <td className="text-end fw-medium">
                                <PriceViewCompact price={parseFloat(item.total_item_cost || 0).toFixed(2)} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="table-light sticky-bottom">
                          <tr>
                            <td colSpan={3} className="text-end fw-bold">Total</td>
                            <td className="text-end fw-bold fs-5 text-primary">
                              <PriceViewCompact price={supplierTotals.cost.toFixed(2)} />
                            </td>
                          </tr>
                        </tfoot>
                      </Table>
                    </div>
                  </CardBody>
                </Card>

                {/* Budget Section Selection (not required for stock-only PO) */}
                {projectId && !isStockOnlyGroup && (
                  <Card className="border-0 shadow-sm">
                    <CardHeader className="bg-light py-2" style={{ backgroundColor: '#f8f9fa' }}>
                      <Label className="mb-0 fw-semibold d-flex align-items-center gap-2">
                        <i className="ri-price-tag-3-line" />
                        Budget section <span className="text-danger">*</span>
                        {budgetData && budgetData.count > 0 && (
                          <Badge color="info" className="ms-2">
                            {budgetData.count} section(s) available
                          </Badge>
                        )}
                      </Label>
                    </CardHeader>
                    <CardBody>
                      {budgetSections.length > 0 ? (
                        <>
                          {/* Search for budget sections */}
                          {budgetData && budgetData.count > budgetPageSize && (
                            <div className="mb-3">
                              <Input
                                type="search"
                                placeholder="Search budget sections..."
                                value={budgetSearch}
                                onChange={(e) => {
                                  setBudgetSearch(e.target.value);
                                  setBudgetPage(1);
                                }}
                                className="form-control-sm"
                              />
                              {budgetTotalPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center mt-2 small text-muted">
                                  <span>Page {budgetPage} of {budgetTotalPages}</span>
                                  <div className="d-flex gap-1">
                                    <Button
                                      color="light"
                                      size="sm"
                                      disabled={budgetPage <= 1}
                                      onClick={() => setBudgetPage(p => Math.max(1, p - 1))}
                                    >
                                      <i className="ri-arrow-left-s-line" />
                                    </Button>
                                    <Button
                                      color="light"
                                      size="sm"
                                      disabled={budgetPage >= budgetTotalPages}
                                      onClick={() => setBudgetPage(p => p + 1)}
                                    >
                                      <i className="ri-arrow-right-s-line" />
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          <Select
                            placeholder="Search and select budget section..."
                            options={budgetOptions}
                            value={selectedOption || null}
                            onChange={(opt: any) => {
                              if (opt) {
                                setSelectedBudgetSectionId(opt.value);
                                setSelectedBudgetSection(opt.section);
                                setPoNotes([""]); // Reset notes when section changes
                              } else {
                                setSelectedBudgetSectionId("");
                                setSelectedBudgetSection(null);
                                setPoNotes([""]);
                              }
                            }}
                            isClearable={false}
                            isSearchable
                            className="react-select-container"
                            classNamePrefix="react-select"
                            noOptionsMessage={() => budgetSearch ? "No sections match your search" : "No budget sections found"}
                            formatOptionLabel={({ label, isOverBudget, isNearLimit, utilizationPercent }: any) => (
                              <div className="d-flex align-items-center justify-content-between">
                                <span>{label}</span>
                                {isOverBudget && (
                                  <Badge color="danger" className="ms-2">
                                    <i className="ri-error-warning-line me-1" /> Over Budget
                                  </Badge>
                                )}
                                {isNearLimit && !isOverBudget && (
                                  <Badge color="warning" className="ms-2">
                                    <i className="ri-alert-line me-1" /> {utilizationPercent.toFixed(0)}%
                                  </Badge>
                                )}
                              </div>
                            )}
                          />

                          {selectedBudgetSection && (
                            <div className="mt-3">
                              {/* Section Details */}
                              <Row className="g-2 mb-3">
                                <Col md={4}>
                                  <div className="p-2 border rounded bg-light">
                                    <small className="text-muted d-block">
                                      <i className="ri-price-tag-3-line align-middle me-1" /> Allocated
                                    </small>
                                    <strong>{parseFloat(selectedBudgetSection.allocated_cost || 0).toFixed(2)}</strong>
                                  </div>
                                </Col>
                                <Col md={4}>
                                  <div className="p-2 border rounded bg-light">
                                    <small className="text-muted d-block">
                                      <i className="ri-wallet-3-line align-middle me-1" /> Actual
                                    </small>
                                    <strong>{parseFloat(selectedBudgetSection.actual_cost || 0).toFixed(2)}</strong>
                                  </div>
                                </Col>
                                <Col md={4}>
                                  <div className={`p-2 border rounded ${exceedsRemaining ? 'bg-danger bg-opacity-10' : 'bg-light'}`}>
                                    <small className="text-muted d-block">
                                      <i className="ri-wallet-2-line align-middle me-1" /> Remaining
                                    </small>
                                    <strong className={exceedsRemaining ? 'text-danger' : 'text-success'}>
                                      {parseFloat(selectedBudgetSection.remaining_cost || 0).toFixed(2)}
                                    </strong>
                                  </div>
                                </Col>
                              </Row>

                              {/* Utilization Progress */}
                              {sectionAllocated > 0 && (
                                <div className="mb-3">
                                  <div className="d-flex justify-content-between small text-muted mb-1">
                                    <span>Section Utilization</span>
                                    <span>{selectedSectionUtilization.toFixed(1)}%</span>
                                  </div>
                                  <Progress
                                    value={selectedSectionUtilization}
                                    color={
                                      isSectionOverBudget
                                        ? "danger"
                                        : selectedSectionUtilization >= 90
                                          ? "warning"
                                          : "success"
                                    }
                                    className="mb-0"
                                    style={{ height: "8px" }}
                                  />
                                </div>
                              )}

                              {/* Warnings */}
                              {exceedsRemaining && (
                                <Alert color="danger" className="d-flex align-items-start">
                                  <i className="ri-error-warning-line fs-5 me-2 mt-1" />
                                  <div className="flex-grow-1">
                                    <strong>Budget Exceeded!</strong> PO total ({supplierTotals.cost.toFixed(2)}) exceeds this section&apos;s remaining budget ({parseFloat(selectedBudgetSection.remaining_cost || 0).toFixed(2)}) by {(supplierTotals.cost - parseFloat(selectedBudgetSection.remaining_cost || 0)).toFixed(2)}.
                                    <div className="mt-2 small">
                                      Please provide justification in the <strong>Over-budget notes</strong> field below.
                                    </div>
                                  </div>
                                </Alert>
                              )}
                              {!exceedsRemaining && selectedSectionUtilization >= 90 && (
                                <Alert color="warning" className="d-flex align-items-start">
                                  <i className="ri-alert-line fs-5 me-2 mt-1" />
                                  <div className="flex-grow-1">
                                    <strong>Budget Warning:</strong> This section is {selectedSectionUtilization.toFixed(1)}% utilized. After this PO, only {(parseFloat(selectedBudgetSection.remaining_cost || 0) - supplierTotals.cost).toFixed(2)} will remain.
                                  </div>
                                </Alert>
                              )}
                              {isSectionOverBudget && (
                                <Alert color="danger" className="d-flex align-items-start">
                                  <i className="ri-error-warning-line fs-5 me-2 mt-1" />
                                  <div className="flex-grow-1">
                                    <strong>Section Already Over Budget:</strong> This section has already exceeded its allocated budget. Actual cost ({parseFloat(selectedBudgetSection.actual_cost || 0).toFixed(2)}) is greater than allocated ({parseFloat(selectedBudgetSection.allocated_cost || 0).toFixed(2)}).
                                  </div>
                                </Alert>
                              )}

                              {/* Over-budget notes (required when PO exceeds budget) */}
                              {(exceedsRemaining || isSectionOverBudget) && (
                                <div className="mt-3">
                                  <Label className="small fw-semibold">
                                    Over-budget notes (reason for exceeding budget) <span className="text-danger">*</span>
                                  </Label>
                                  <Input
                                    type="textarea"
                                    rows={3}
                                    placeholder="Explain why this purchase order needs to exceed the budget section..."
                                    value={overBudgetNotes}
                                    onChange={(e) => setOverBudgetNotes(e.target.value)}
                                    invalid={!overBudgetNotes.trim()}
                                  />
                                </div>
                              )}

                              {/* Global PO notes / instructions (required, multiple entries) */}
                              <div className="mt-3">
                                <Label className="small fw-semibold d-flex justify-content-between align-items-center">
                                  <span>
                                    PO Notes / Instructions <span className="text-danger">*</span>
                                  </span>
                                  <Button
                                    size="sm"
                                    color="outline-primary"
                                    type="button"
                                    onClick={() => setPoNotes((prev) => [...prev, ""])}
                                  >
                                    <i className="ri-add-line me-1" />
                                    Add Note
                                  </Button>
                                </Label>

                                {poNotes.map((note, idx) => {
                                  const hasError = poNotes.every((n) => !n.trim());
                                  return (
                                    <div className="d-flex align-items-start mt-2" key={idx}>
                                      <Input
                                        type="textarea"
                                        rows={2}
                                        placeholder={`Note ${idx + 1}`}
                                        value={note}
                                        onChange={(e) => {
                                          const value = e.target.value;
                                          setPoNotes((prev) => {
                                            const copy = [...prev];
                                            copy[idx] = value;
                                            return copy;
                                          });
                                        }}
                                        className="mt-0"
                                        invalid={hasError}
                                      />
                                      {poNotes.length > 1 && (
                                        <Button
                                          size="sm"
                                          color="link"
                                          type="button"
                                          className="ms-2 text-danger"
                                          onClick={() =>
                                            setPoNotes((prev) =>
                                              prev.filter((_, i) => i !== idx)
                                            )
                                          }
                                        >
                                          <i className="ri-close-circle-line" />
                                        </Button>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <Alert color="info">
                          <i className="ri-information-line me-2" />
                          No budget sections found for this project. Please create budget sections in the Project Budget tab first.
                        </Alert>
                      )}
                    </CardBody>
                  </Card>
                )}

                {isStockOnlyGroup && (
                  <Alert color="success">
                    <i className="ri-checkbox-circle-line me-2" />
                    This is a stock-only transfer PO. Approval workflow and budget mapping are skipped.
                  </Alert>
                )}

                {getItemsWithExistingPO(groupedItems[selectedSupplier]).length > 0 && (
                  <Alert color="info" className="mt-3 mb-0">
                    <i className="ri-information-line align-middle me-2"></i>
                    Items that already have POs are excluded from this order.
                  </Alert>
                )}
              </>
            );
          })()}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleCloseConfirmation} disabled={isCreatingPO}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleConfirmCreatePO}
            disabled={
              isCreatingPO ||
              (selectedSupplier !== STOCK_ONLY_GROUP_KEY &&
                budgetData &&
                budgetData.count > 0 &&
                !selectedBudgetSectionId) ||
              (selectedSupplier &&
                groupedItems[selectedSupplier] &&
                selectedSupplier !== STOCK_ONLY_GROUP_KEY &&
                selectedBudgetSection &&
                (() => {
                  const eligibleItems = getEligibleItemsForPO(groupedItems[selectedSupplier]);
                  const tot = calculateSupplierTotals(eligibleItems).cost;
                  const rem = parseFloat(selectedBudgetSection.remaining_cost || 0);
                  const allocated = parseFloat(selectedBudgetSection.allocated_cost || 0);
                  const actual = parseFloat(selectedBudgetSection.actual_cost || 0);
                  const isSectionAlreadyOverBudget = allocated > 0 && actual > allocated;
                  const isExceedingNow = tot > rem;
                  const requiresOverBudgetNotes = isSectionAlreadyOverBudget || isExceedingNow;
                  const normalizedNotes = poNotes
                    .map((n) => n.trim())
                    .filter((n) => n.length > 0);
                  // When PO is over budget, require both at least one normal note AND a dedicated over-budget note
                  if (!requiresOverBudgetNotes) {
                    return normalizedNotes.length === 0;
                  }
                  return normalizedNotes.length === 0 || !overBudgetNotes.trim();
                })())
            }
          >
            {isCreatingPO ? (
              <>
                <Spinner size="sm" className="me-2" />
                Creating...
              </>
            ) : (
              <>
                <i className="ri-check-line align-middle me-1"></i>
                Confirm & Create Purchase Order
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default PreviewSection;